import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Plus, FlaskConical, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../api';
import { ScheduleWorkshopModal } from '../components/modals/ScheduleWorkshopModal';
import { AttendanceModal } from '../components/modals/AttendanceModal';
import { EditWorkshopModal } from '../components/modals/EditWorkshopModal';
import { ConfirmDeleteModal } from '../components/modals/ConfirmDeleteModal';
import { PastDueReminder } from '../components/widgets/PastDueReminder';

const WorkshopsManagement = ({ user }) => {
  const { t } = useTranslation();
  const [workshops, setWorkshops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showAttendanceModal, setShowAttendanceModal] = useState(null);
  const [showEditModal, setShowEditModal] = useState(null);
  const [courseFilter, setCourseFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [editingLabConsumption, setEditingLabConsumption] = useState(null); // { workshopId, value }
  const [savingLabConsumption, setSavingLabConsumption] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null); // { ids: [...] }
  const [deleting, setDeleting] = useState(false);
  const [orgTrainers, setOrgTrainers] = useState([]);
  const isAdmin = user.role === 'admin';

  useEffect(() => {
    fetchWorkshops();
    if (user.role === 'partner_admin') {
      api.request('/partner-admin/setup-status')
        .then(data => {
          const trainers = (data.designated_trainers || []).filter(t => t.is_approved && !t.pending_registration);
          setOrgTrainers([{ id: user.id, email: user.email, full_name: user.full_name, role: 'partner_admin' }, ...trainers]);
        })
        .catch(() => setOrgTrainers([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchWorkshops = async () => {
    setLoading(true);
    try {
      const data = await api.request('/workshops');
      setWorkshops(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Get unique values for filter dropdowns
  const uniqueCourses = [...new Set(workshops.map(e => e.course_title).filter(Boolean))];
  const uniqueLocations = [...new Set(workshops.map(e => e.location).filter(Boolean))];

  // Filter workshops (filters only for admin/ms_stakeholder, not partner_admin)
  const filteredWorkshops = workshops.filter(ws => {
    const matchesCourse = !courseFilter || ws.course_title === courseFilter;
    const matchesLocation = !locationFilter || ws.location === locationFilter;
    const matchesStatus = !statusFilter || getWorkshopStatus(ws) === statusFilter;
    return matchesCourse && matchesLocation && matchesStatus;
  });

  const statusColors = {
    scheduled: 'bg-blue-100 text-blue-700',
    completed: 'bg-green-100 text-green-700',
    cancelled: 'bg-red-100 text-red-700',
    'past due': 'bg-red-500 text-white'
  };

  const getWorkshopStatus = (ws) => {
    if (ws.status === 'scheduled') {
      const workshopDate = new Date(ws.delivery_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (workshopDate < today) {
        return 'past due';
      }
    }
    return ws.status;
  };

  // Check if user can submit attendance for a workshop
  // Partner Admin can submit for their own workshops or their trainers' workshops
  // Attendance can only be submitted after the workshop date has passed
  const canSubmitAttendance = (ws) => {
    if (ws.attendance_submitted) return false;
    if (ws.status !== 'scheduled') return false;
    
    // Check if workshop date has passed
    const workshopDate = new Date(ws.delivery_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (workshopDate >= today) return false; // Can't submit attendance for future/today workshops
    
    if (user.role === 'training_partner') return true;
    if (user.role === 'partner_admin') return true; // Can submit for any org workshop
    return false;
  };

  // Check if user can edit a workshop (change date or cancel)
  const canEditWorkshop = (ws) => {
    if (ws.attendance_submitted) return false; // Can't edit after attendance submitted
    if (ws.status === 'cancelled') return false; // Already cancelled
    if (ws.status === 'completed') return false; // Already completed
    
    // Only training partners and partner admins can edit workshops
    if (user.role === 'training_partner') {
      return ws.user_id === user.id; // Can only edit own workshops
    }
    if (user.role === 'partner_admin') {
      return true; // Can edit any org workshop
    }
    return false;
  };

  // Determine page title and whether to show trainer column
  const isPartnerView = ['training_partner', 'partner_admin'].includes(user.role);
  const showTrainerColumn = user.role === 'partner_admin';
  const showScheduleButton = ['training_partner', 'partner_admin'].includes(user.role);

  const saveLabConsumption = async () => {
    if (!editingLabConsumption) return;
    setSavingLabConsumption(true);
    try {
      await api.request(`/workshops/${editingLabConsumption.workshopId}/lab-consumption`, {
        method: 'PUT',
        body: JSON.stringify({ lab_consumption: editingLabConsumption.value === '' ? null : parseInt(editingLabConsumption.value) })
      });
      fetchWorkshops();
      setEditingLabConsumption(null);
    } catch (err) {
      alert(err.message);
    } finally {
      setSavingLabConsumption(false);
    }
  };
  
  // Page title based on role
  const getPageTitle = () => {
    if (user.role === 'training_partner') return t('workshops.title');
    if (user.role === 'partner_admin') return t('workshops.myOrgWorkshops');
    return t('workshops.allWorkshops');
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredWorkshops.length) setSelectedIds([]);
    else setSelectedIds(filteredWorkshops.map(w => w.id));
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      if (deleteTarget.ids.length === 1) {
        await api.request(`/workshops/${deleteTarget.ids[0]}`, { method: 'DELETE' });
      } else {
        await api.request('/workshops/bulk-delete', {
          method: 'POST',
          body: JSON.stringify({ ids: deleteTarget.ids }),
        });
      }
      toast.success(`${deleteTarget.ids.length} workshop(s) moved to Deleted Items.`);
      setSelectedIds(prev => prev.filter(id => !deleteTarget.ids.includes(id)));
      setDeleteTarget(null);
      fetchWorkshops();
    } catch (err) {
      toast.error(err.message || 'Failed to delete workshop(s).');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="bg-levelup-dark rounded-xl p-4 text-white flex-1 mr-4">
          <h1 className="text-xl font-bold">{getPageTitle()}</h1>
          <p className="text-gray-300 mt-1">{t('workshops.subtitle')}</p>
        </div>
        {showScheduleButton && (
          <button
            onClick={() => setShowScheduleModal(true)}
            className="bg-levelup-accent text-levelup-darker px-3 py-2 rounded-lg font-semibold hover:bg-levelup-accent-hover flex items-center space-x-2"
            data-testid="schedule-workshop-button"
          >
            <Plus size={20} />
            <span>{t('workshops.schedule')}</span>
          </button>
        )}
      </div>

      {isAdmin && selectedIds.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center justify-between" data-testid="workshops-bulk-action-bar">
          <span className="text-sm text-red-700 font-medium">{selectedIds.length} workshop(s) selected</span>
          <div className="flex items-center gap-2">
            <button onClick={() => setSelectedIds([])} className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800">
              Clear selection
            </button>
            <button
              onClick={() => setDeleteTarget({ ids: selectedIds })}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700"
              data-testid="bulk-delete-workshops-btn"
            >
              <Trash2 size={14} /> Delete Selected
            </button>
          </div>
        </div>
      )}

      {/* Filters - only show for admin/ms_stakeholder (NOT partner_admin) */}
      {['admin', 'ms_stakeholder'].includes(user.role) && (
        <div className="bg-white rounded-xl shadow-md p-4">
          <div className="flex flex-wrap gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('courses.title')}</label>
              <select
                value={courseFilter}
                onChange={(e) => setCourseFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-levelup-accent focus:border-transparent"
              >
                <option value="">{t('courses.allCategories')}</option>
                {uniqueCourses.map(course => (
                  <option key={course} value={course}>{course}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('workshops.location')}</label>
              <select
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-levelup-accent focus:border-transparent"
              >
                <option value="">All Locations</option>
                {uniqueLocations.map(location => (
                  <option key={location} value={location}>{location}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('workshops.status')}</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-levelup-accent focus:border-transparent"
              >
                <option value="">All Statuses</option>
                <option value="scheduled">{t('workshops.scheduled')}</option>
                <option value="completed">{t('workshops.completed')}</option>
                <option value="past due">{t('workshops.pastDue')}</option>
              </select>
            </div>
            {(courseFilter || locationFilter || statusFilter) && (
              <div className="flex items-end">
                <button
                  onClick={() => { setCourseFilter(''); setLocationFilter(''); setStatusFilter(''); }}
                  className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg"
                >
                  {t('common.clear')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin w-8 h-8 border-4 border-levelup-accent border-t-transparent rounded-full mx-auto"></div>
          </div>
        ) : filteredWorkshops.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            {t('workshops.noWorkshopsFound')}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  {isAdmin && (
                    <th className="px-3 py-2 w-8">
                      <input
                        type="checkbox"
                        checked={filteredWorkshops.length > 0 && selectedIds.length === filteredWorkshops.length}
                        onChange={toggleSelectAll}
                        data-testid="select-all-workshops-checkbox"
                      />
                    </th>
                  )}
                  <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">{t('workshops.course')}</th>
                  {showTrainerColumn && (
                    <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">{t('workshops.trainer')}</th>
                  )}
                  {!isPartnerView && (
                    <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">Partner Domain</th>
                  )}
                  <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">{t('workshops.date')}</th>
                  <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">{t('workshops.location')}</th>
                  <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">{t('workshops.expectedAttendees')}</th>
                  <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">{t('workshops.actualAttendees')}</th>
                  {isPartnerView && (
                    <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">Lab Request</th>
                  )}
                  <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">Lab Consumption</th>
                  <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">{t('workshops.status')}</th>
                  {isPartnerView && (
                    <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">Actions</th>
                  )}
                  {isAdmin && (
                    <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredWorkshops.map(ws => {
                  const displayStatus = getWorkshopStatus(ws);
                  return (
                  <tr key={ws.id} data-testid={`workshop-row-${ws.id}`}>
                    {isAdmin && (
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(ws.id)}
                          onChange={() => toggleSelect(ws.id)}
                          data-testid={`select-workshop-${ws.id}`}
                        />
                      </td>
                    )}
                    <td className="px-3 py-2 font-medium">{ws.course_title}</td>
                    {showTrainerColumn && (
                      <td className="px-3 py-2 text-gray-600">{ws.trainer_name || ws.trainer_email || '-'}</td>
                    )}
                    {!isPartnerView && (
                      <td className="px-3 py-2 text-gray-600">{ws.partner_domain || '-'}</td>
                    )}
                    <td className="px-3 py-2 text-gray-600">
                      {new Date(ws.delivery_date).toLocaleDateString()}
                    </td>
                    <td className="px-3 py-2 text-gray-600">{ws.location}</td>
                    <td className="px-3 py-2 text-gray-600">{ws.expected_attendees}</td>
                    <td className="px-3 py-2 text-gray-600">
                      {ws.actual_attendees || '-'}
                    </td>
                    {isPartnerView && (
                      <td className="px-3 py-2">
                        {ws.lab_request_status ? (
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold capitalize ${
                            ws.lab_request_status === 'approved' ? 'bg-green-100 text-green-700' :
                            ws.lab_request_status === 'rejected' ? 'bg-red-100 text-red-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`} data-testid={`lab-request-status-${ws.id}`}>
                            {ws.lab_request_status}
                          </span>
                        ) : (
                          <span className="text-gray-300 text-xs">—</span>
                        )}
                      </td>
                    )}
                    <td className="px-3 py-2 text-gray-600">
                      {user.role === 'admin' ? (
                        editingLabConsumption?.workshopId === ws.id ? (
                          <div className="flex items-center gap-1">
                            <input
                              type="number"
                              min="0"
                              className="w-20 px-2 py-1 border border-blue-300 rounded text-sm focus:ring-1 focus:ring-blue-400"
                              value={editingLabConsumption.value}
                              onChange={e => setEditingLabConsumption(prev => ({...prev, value: e.target.value}))}
                              autoFocus
                              data-testid={`lab-consumption-input-${ws.id}`}
                            />
                            <button onClick={saveLabConsumption} disabled={savingLabConsumption} className="px-2 py-1 bg-green-500 text-white rounded text-xs hover:bg-green-600" data-testid={`save-lab-consumption-${ws.id}`}>
                              {savingLabConsumption ? '...' : 'Save'}
                            </button>
                            <button onClick={() => setEditingLabConsumption(null)} className="px-2 py-1 bg-gray-200 text-gray-600 rounded text-xs hover:bg-gray-300">
                              ✕
                            </button>
                          </div>
                        ) : (
                          <button
                            className="flex items-center gap-1 text-sm text-gray-700 hover:text-blue-600 group"
                            onClick={() => setEditingLabConsumption({ workshopId: ws.id, value: ws.lab_consumption ?? '' })}
                            data-testid={`edit-lab-consumption-${ws.id}`}
                          >
                            <span>{ws.lab_consumption ?? '—'}</span>
                            <span className="text-xs text-blue-400 opacity-0 group-hover:opacity-100">(edit)</span>
                          </button>
                        )
                      ) : (
                        <span>{ws.lab_consumption ?? '—'}</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[displayStatus]}`}>
                        {displayStatus}
                      </span>
                    </td>
                    {isAdmin && (
                      <td className="px-3 py-2">
                        <button
                          onClick={() => setDeleteTarget({ ids: [ws.id] })}
                          className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete workshop"
                          data-testid={`delete-workshop-${ws.id}`}
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    )}
                    {isPartnerView && (
                      <td className="px-3 py-2">
                        <div className="flex items-center space-x-2">
                          {canSubmitAttendance(ws) && (
                            <button
                              onClick={() => setShowAttendanceModal(ws)}
                              className="px-3 py-1 bg-levelup-accent text-levelup-darker rounded text-sm hover:bg-levelup-accent-hover"
                              data-testid={`submit-attendance-${ws.id}`}
                            >
                              {t('workshops.submitAttendance')}
                            </button>
                          )}
                          {canEditWorkshop(ws) && (
                            <button
                              onClick={() => setShowEditModal(ws)}
                              className="px-3 py-1 bg-gray-100 text-gray-700 rounded text-sm hover:bg-gray-200"
                              data-testid={`edit-workshop-${ws.id}`}
                            >
                              {t('common.edit')}
                            </button>
                          )}
                          {ws.attendance_submitted && (
                            <span className="text-green-600 text-sm flex items-center">
                              <CheckCircle size={14} className="mr-1" /> {t('workshops.attendanceSubmitted')}
                            </span>
                          )}
                          {ws.status === 'cancelled' && (
                            <span className="text-red-600 text-sm">{t('workshops.cancelled')}</span>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showScheduleModal && (
        <ScheduleWorkshopModal
          onClose={() => setShowScheduleModal(false)}
          onSuccess={() => { setShowScheduleModal(false); fetchWorkshops(); }}
        />
      )}

      {showAttendanceModal && (
        <AttendanceModal
          workshop={showAttendanceModal}
          onClose={() => setShowAttendanceModal(null)}
          onSuccess={() => { setShowAttendanceModal(null); fetchWorkshops(); }}
        />
      )}

      {showEditModal && (
        <EditWorkshopModal
          workshop={showEditModal}
          orgTrainers={user.role === 'partner_admin' ? orgTrainers : []}
          onClose={() => setShowEditModal(null)}
          onSuccess={() => { setShowEditModal(null); fetchWorkshops(); }}
        />
      )}

      <ConfirmDeleteModal
        open={!!deleteTarget}
        title="Delete workshop(s)?"
        itemCount={deleteTarget?.ids.length || 0}
        deleting={deleting}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
};

export default WorkshopsManagement;
